import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export interface LeaveBalancePayload {
  _id: string;
  firstName: string;
  lastName: string;
  org: string;
  email: string;
}

export interface LeaveBalance {
  userId: string;
  annual: {
    allocated: number;
    used: number;
    pending?: number;
    balance: number;
  };
  sick?: {
    allocated?: number;
    used?: number;
    balance?: number;
  };
  [key: string]: any;
}

export type LeaveBalanceResponse = {
  success: boolean;
  message: string;
  data: LeaveBalance;
};

const addLeaveBalance = async (
  payload: LeaveBalancePayload
): Promise<LeaveBalanceResponse> => {
  const { data } = await customAxios.put<LeaveBalanceResponse>(
    `/leaves/leave-balance/${payload._id}`,
    payload
  );
  return data;
};

export const useAddLeaveBalance = () => {
  const queryClient = useQueryClient();

  return useMutation<LeaveBalanceResponse, AxiosError, LeaveBalancePayload>({
    mutationFn: addLeaveBalance,
    onSuccess: async (data, variables) => {
      showNotification({
        title: "Success",
        message: data.message,
        color: "green",
      });

      await queryClient.invalidateQueries([
        "users",
        variables._id,
        "leaveBalance",
      ]);
      await queryClient.invalidateQueries(["leaveBalances"]);
    },
    onError: (error) =>
      showNotification({
        title: "Error",
        message:
          (error.response?.data as { message?: string })?.message ??
          "Failed to update leave balance",
        color: "red",
      }),
  });
};
